import { createSlice } from '@reduxjs/toolkit';

// Job state management for all asynchronous operations
// This slice tracks long-running jobs across the application to prevent state loss on navigation

const initialState = {
  // Jobs are keyed by featureType to ensure one active job per feature
  variationGenerator: {
    status: 'idle', // 'idle' | 'pending' | 'polling' | 'succeeded' | 'failed'
    jobId: null,    // The ID returned by our backend (e.g., Replicate prediction ID)
    progress: 0,    // Progress percentage (0-100)
    error: null,    // Error message if the job failed
    result: null,   // The final result of the job (e.g., array of image URLs)
    startedAt: null, // Timestamp when job started
    completedAt: null, // Timestamp when job completed
    estimatedDuration: 120000, // Estimated duration in ms (2 minutes for variations)
    retryCount: 0,  // Number of retry attempts
    metadata: {}    // Feature-specific metadata
  },
  photoshoot: {
    status: 'idle',
    jobId: null,
    progress: 0,
    error: null,
    result: null,
    startedAt: null,
    completedAt: null,
    estimatedDuration: 600000, // 10 minutes for photoshoots
    retryCount: 0,
    metadata: {}
  },
  fashion_photoshoot: {
    status: 'idle',
    jobId: null,
    progress: 0,
    error: null,
    result: null,
    startedAt: null,
    completedAt: null,
    estimatedDuration: 600000, // 10 minutes for e-commerce photoshoots
    retryCount: 0,
    metadata: {}
  },
  moodboard: {
    status: 'idle',
    jobId: null,
    progress: 0,
    error: null,
    result: null,
    startedAt: null,
    completedAt: null,
    estimatedDuration: 180000, // 3 minutes for AI Designer
    retryCount: 0,
    metadata: {}
  },
  productEditor: {
    status: 'idle',
    jobId: null,
    progress: 0,
    error: null,
    result: null,
    startedAt: null,
    completedAt: null,
    estimatedDuration: 60000, // 1 minute for product editor
    retryCount: 0,
    metadata: {}
  },
  colorChanger: {
    status: 'idle',
    jobId: null,
    progress: 0,
    error: null,
    result: null,
    startedAt: null,
    completedAt: null,
    estimatedDuration: 120000, // 2 minutes for color changer
    retryCount: 0,
    metadata: {}
  },
  color_changer: {
    status: 'idle',
    jobId: null,
    progress: 0,
    error: null,
    result: null,
    startedAt: null,
    completedAt: null,
    estimatedDuration: 120000, // 2 minutes for color changer
    retryCount: 0,
    metadata: {}
  },
  techpack: {
    status: 'idle',
    jobId: null,
    progress: 0,
    currentStep: '', // Progress message from backend
    error: null,
    result: null,
    startedAt: null,
    completedAt: null,
    estimatedDuration: 90000, // 1.5 minutes for tech pack
    retryCount: 0,
    metadata: {}
  },
  productView: {
    status: 'idle',
    jobId: null,
    progress: 0,
    error: null,
    result: null,
    startedAt: null,
    completedAt: null,
    estimatedDuration: 120000, // 2 minutes for product view
    retryCount: 0,
    metadata: {}
  },
  productTagger: {
    status: 'idle',
    jobId: null,
    progress: 0,
    error: null,
    result: null,
    startedAt: null,
    completedAt: null,
    estimatedDuration: 300000, // 5 minutes for batch tagging
    retryCount: 0,
    metadata: {}
  },
  product_editor: {
    status: 'idle',
    jobId: null,
    progress: 0,
    error: null,
    result: null,
    startedAt: null,
    completedAt: null,
    estimatedDuration: 60000, // 1 minute for garment editing
    retryCount: 0,
    metadata: {}
  },
  instagram_photoshoot: {
    status: 'idle',
    jobId: null,
    progress: 0,
    error: null,
    result: null,
    startedAt: null,
    completedAt: null,
    estimatedDuration: 300000, // 5 minutes for Instagram photoshoot
    retryCount: 0,
    metadata: {}
  },
  refabric: {
    status: 'idle',
    jobId: null,
    progress: 0,
    error: null,
    result: null,
    startedAt: null,
    completedAt: null,
    estimatedDuration: 120000, // 2 minutes for fabric swap
    retryCount: 0,
    metadata: {}
  }
};

const jobsSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    // Start a new job for a feature
    startJob: (state, action) => {
      const { featureType, jobId, metadata = {} } = action.payload;
      
      // Initialize the feature state if it doesn't exist (defensive fix for deployment issues)
      if (!state[featureType]) {
        console.warn(`Feature type ${featureType} not found in Redux state during startJob, initializing...`);
        state[featureType] = {
          status: 'idle',
          jobId: null,
          progress: 0,
          error: null,
          result: null,
          startedAt: null,
          completedAt: null,
          estimatedDuration: 300000,
          retryCount: 0,
          metadata: {}
        };
      }
      
      state[featureType] = {
        ...state[featureType], // Use current state as base
        status: 'pending',
        jobId,
        startedAt: new Date().toISOString(),
        metadata,
        error: null,
        result: null
      };
    },

    // Update job progress
    updateJobProgress: (state, action) => {
      const { featureType, progress, status = 'polling', currentStep } = action.payload;
      if (state[featureType]) {
        state[featureType].progress = Math.min(progress, 99); // Cap at 99 until complete
        state[featureType].status = status;
        if (currentStep !== undefined) {
          state[featureType].currentStep = currentStep;
        }
      }
    },

    // Mark job as succeeded
    jobSucceeded: (state, action) => {
      const { featureType, result } = action.payload;
      console.log(`jobSucceeded reducer called for ${featureType} (result: ${result ? (Array.isArray(result) ? `${result.length} items` : 'object') : 'none'})`);
      
      // Initialize the feature state if it doesn't exist (defensive fix for deployment issues)
      if (!state[featureType]) {
        console.warn(`Feature type ${featureType} not found in Redux state, initializing...`);
        state[featureType] = {
          status: 'idle',
          jobId: null,
          progress: 0,
          error: null,
          result: null,
          startedAt: null,
          completedAt: null,
          estimatedDuration: 300000,
          retryCount: 0,
          metadata: {}
        };
      }
      
      state[featureType] = {
        ...state[featureType],
        status: 'succeeded',
        progress: 100,
        result,
        completedAt: new Date().toISOString(),
        error: null
      };
      console.log(`Updated Redux state for ${featureType}:`, state[featureType]);
    },

    // Mark job as failed
    jobFailed: (state, action) => {
      const { featureType, error, canRetry = true } = action.payload;
      if (state[featureType]) {
        state[featureType] = {
          ...state[featureType],
          status: 'failed',
          error,
          completedAt: new Date().toISOString(),
          retryCount: state[featureType].retryCount + (canRetry ? 1 : 0)
        };
      }
    },

    // Clear/reset a job
    clearJob: (state, action) => {
      const { featureType } = action.payload;
      if (state[featureType]) {
        state[featureType] = initialState[featureType];
      }
    },

    // Update job metadata (for feature-specific data)
    updateJobMetadata: (state, action) => {
      const { featureType, metadata } = action.payload;
      if (state[featureType]) {
        state[featureType].metadata = {
          ...state[featureType].metadata,
          ...metadata
        };
      }
    },

    // Clean up old completed/failed jobs (for maintenance)
    cleanupOldJobs: (state) => {
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      
      Object.keys(state).forEach(featureType => {
        const job = state[featureType];
        if (
          (job.status === 'succeeded' || job.status === 'failed') &&
          job.completedAt &&
          job.completedAt < oneHourAgo
        ) {
          state[featureType] = initialState[featureType];
        }
      });
    }
  }
});

// Export actions
export const {
  startJob,
  updateJobProgress,
  jobSucceeded,
  jobFailed,
  clearJob,
  updateJobMetadata,
  cleanupOldJobs
} = jobsSlice.actions;

// Selectors
export const selectJobByFeature = (state, featureType) => state.jobs[featureType];
export const selectAllJobs = (state) => state.jobs;
export const selectActiveJobs = (state) => {
  const jobs = state.jobs;
  return Object.entries(jobs)
    .filter(([_, job]) => job.status === 'pending' || job.status === 'polling')
    .reduce((acc, [featureType, job]) => ({ ...acc, [featureType]: job }), {});
};

// Helper to calculate estimated time remaining
export const selectEstimatedTimeRemaining = (state, featureType) => {
  const job = state.jobs[featureType];
  if (!job || job.status !== 'polling' || !job.startedAt) return null;
  
  const elapsed = Date.now() - new Date(job.startedAt).getTime();
  const estimated = job.estimatedDuration;
  const remaining = Math.max(0, estimated - elapsed);
  
  // If we're past the estimate but still polling, show indeterminate
  if (remaining === 0 && job.progress < 100) {
    return -1; // Indicates "taking longer than expected"
  }
  
  return remaining;
};

export default jobsSlice.reducer;